<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Pagos / Colegiatura / Calendario</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="noDataAvailable" class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Sin información disponible
          </div>

          <div v-else-if="data && data.length > 0" class="mb-6">
            <h2 class="text-lg font-semibold mb-2">Cargos de Colegiatura por Pagar:</h2>
            
            <!-- Botones de Pago -->
            <div v-if="selectedItems.length > 0" class="mb-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button 
                @click="showPaymentModal('bank')" 
                class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                Pagar en línea - Total: {{ formatCurrency(calculateTotal()) }}
              </button>
              <button 
                @click="showPaymentModal('cash')" 
                class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
              >
                Pagar en OXXO - Total: {{ formatCurrency(calculateTotal()) }}
              </button>
            </div>

            <!-- Vista móvil: tarjetas individuales -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <div 
                v-for="item in data" 
                :key="item.idPeriod"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 cursor-pointer"
                :class="[
                  isPastDate(item.week_payment_date) ? 'border-red-500 bg-red-50' : 'border-blue-500',
                  selectedItems.includes(item) ? 'ring-2 ring-blue-500 bg-blue-50' : '',
                  item.hasPaymentLink ? 'opacity-50 pointer-events-none' : ''
                ]"
                @click="!item.hasPaymentLink ? toggleItemSelection(item) : null"
              >
                <div class="flex items-center mb-2" v-if="!item.hasPaymentLink">
                  <input 
                    type="checkbox" 
                    :checked="selectedItems.includes(item)"
                    class="mr-2 h-4 w-4"
                  />
                  <span class="text-sm font-medium">Seleccionar</span>
                </div>
                <div v-else class="mb-2">
                  <span class="text-sm font-medium text-green-600">✓ Liga generada</span>
                </div>
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Fecha de Pago:</div>
                  <div class="text-sm font-bold">{{ formatDate(item.week_payment_date) }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Descripción:</div>
                  <div class="text-sm">{{ item.description }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Por Pagar:</div>
                  <div class="text-sm font-bold">{{ formatCurrency(item.balance) }}</div>
                </div>
              </div>
            </div>
            
            <!-- Vista escritorio: tabla tradicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="w-12 pl-4 pr-3">
                      <input 
                        type="checkbox" 
                        :checked="availableItems.length > 0 && availableItems.length === selectedItems.length"
                        @change="toggleSelectAll"
                        class="h-4 w-4"
                        :disabled="availableItems.length === 0"
                      />
                    </th>
                    <th scope="col" class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Fecha de Pago</th>
                    <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Descripción</th>
                    <th scope="col" class="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Por Pagar</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                  <tr 
                    v-for="item in data" 
                    :key="item.idPeriod"
                    :class="[
                      isPastDate(item.week_payment_date) ? 'bg-red-400' : '',
                      selectedItems.includes(item) ? 'bg-blue-50' : '',
                      item.hasPaymentLink ? 'bg-green-50 opacity-75' : ''
                    ]"
                    @click="!item.hasPaymentLink ? toggleItemSelection(item) : null"
                    class="cursor-pointer hover:bg-gray-100"
                  >
                    <td class="w-12 pl-4 pr-3">
                      <input 
                        v-if="!item.hasPaymentLink"
                        type="checkbox" 
                        :checked="selectedItems.includes(item)"
                        class="h-4 w-4"
                      />
                      <span v-else class="text-green-600 font-bold">✓</span>
                    </td>
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {{ formatDate(item.week_payment_date) }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm">
                      {{ item.description }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-right">
                      {{ formatCurrency(item.balance) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Modal de Confirmación de Pago Estilo SweetAlert -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 swal2-container" @click="closeModal">
      <!-- Modal Container -->
      <div class="relative bg-white rounded-lg shadow-2xl max-w-lg w-full mx-auto" style="max-width: 32rem;" @click.stop>
        
        <!-- Icono de Pregunta (Estilo SweetAlert) -->
        <div class="text-center">     
          <div class="swal2-icon swal2-question swal2-icon-show" style="display: flex;"><div class="swal2-icon-content">?</div></div>
          <!-- Título Principal -->
          <h2 class="mb-2 swal2-title">Confirmar Pago</h2>

          <!-- Texto de Confirmación -->
          <p class="text-gray-600 text-lg px-4 mb-6">
            ¿Deseas realizar un pago de 
            <span class="font-bold text-gray-900">{{ formatCurrency(calculateTotal()) }}</span> 
            mediante {{ modalPaymentType === 'bank' ? 'Pago en Línea' : 'Pago en OXXO' }}?
          </p>
        </div>

        <!-- Sección de Facturación (Entre texto y botones) -->
        <div class="px-6 pb-6">
          <!-- Toggle de Facturación -->
          <div class="bg-gray-50 border rounded-lg p-4 mb-4">
            <div class="flex flex-col space-y-3">
              <div class="flex items-center justify-center">
                <label class="flex items-center cursor-pointer">
                  <div class="relative">
                    <input 
                      type="checkbox" 
                      v-model="requiresInvoice" 
                      class="sr-only"
                    />
                    <div 
                      :class="[
                        'block w-12 h-6 rounded-full transition-colors duration-200',
                        requiresInvoice ? 'bg-blue-600' : 'bg-gray-300'
                      ]"
                    ></div>
                    <div 
                      :class="[
                        'absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200',
                        requiresInvoice ? 'transform translate-x-6' : ''
                      ]"
                    ></div>
                  </div>
                  <span class="ml-3 text-base font-medium text-gray-700">
                    Requiero factura
                  </span>
                </label>
              </div>
              
              <div v-if="requiresInvoice && billingDataStatus === 'loaded'" class="text-center">
                <div class="text-sm text-green-600 flex items-center justify-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Datos fiscales disponibles
                </div>
              </div>
            </div>

            <!-- Información de datos fiscales cuando se requiere factura -->
            <div v-if="requiresInvoice" class="mt-4 pt-3 border-t border-gray-200">
              <div v-if="billingDataStatus === 'loading'" class="text-center">
                <div class="flex items-center justify-center text-sm text-gray-600">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Verificando datos fiscales...
                </div>
              </div>
              
              <div v-else-if="billingDataStatus === 'no-data'" class="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
                <div class="text-center">
                  <div class="flex items-center justify-center mb-2">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                    <span class="font-medium">No tienes datos fiscales registrados</span>
                  </div>
                  <button 
                    @click="goToBillingDataFromModal" 
                    class="text-blue-700 underline hover:text-blue-800 font-medium"
                  >
                    Configurar mis datos fiscales
                  </button>
                </div>
              </div>
              
              <div v-else-if="billingDataStatus === 'outdated'" class="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded text-sm">
                <div class="text-center">
                  <div class="flex items-center justify-center mb-2">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span class="font-medium">Datos fiscales desactualizados</span>
                  </div>
                  <p class="text-xs mb-2">Última actualización: {{ formatDate(billingData?.updated) }}</p>
                  <button 
                    @click="goToBillingDataFromModal" 
                    class="text-blue-700 underline hover:text-blue-800 font-medium"
                  >
                    Actualizar datos fiscales
                  </button>
                </div>
              </div>
              
              <div v-else-if="billingDataStatus === 'loaded' && billingData && billingData.length > 0" class="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm">
                <div class="text-center space-y-2">
                  <p class="font-medium flex items-center justify-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Datos fiscales disponibles
                  </p>
                  <div class="text-xs space-y-1">
                    <div><strong>RFC:</strong> {{ billingData[0].rfc }}</div>
                    <div><strong>Régimen Fiscal:</strong> {{ getRegimenTexto(billingData[0].idregimen) }}</div>
                    <div><strong>Email:</strong> {{ billingData[0].email }}</div>
                  </div>
                  <button 
                    @click="goToBillingDataFromModal" 
                    class="text-blue-700 underline hover:text-blue-800 text-xs"
                  >
                    Modificar datos fiscales
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botones Estilo SweetAlert -->
        <div class="flex justify-center space-x-3 pb-8 px-6">
          <button 
            @click="confirmPayment"
            :disabled="requiresInvoice && billingDataStatus !== 'loaded'"
            :class="[
              'px-6 py-2 text-white font-medium rounded-md transition-colors text-base',
              (requiresInvoice && billingDataStatus !== 'loaded') 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-500 hover:bg-green-600'
            ]"
          >
            Sí, proceder
          </button>
          <button 
            @click="closeModal"
            class="px-6 py-2 bg-red-500 text-white font-medium rounded-md hover:bg-red-600 transition-colors text-base"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getCalendarioColegiatura, getBillingData } from "@/services/api";

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      data: null,
      loading: true,
      error: null,
      noDataAvailable: false,
      studentId: null,
      selectedItems: [], // Array para acumular
      requiresInvoice: false, // Toggle para requerir factura
      billingData: null,
      billingDataStatus: 'loading',
      showModal: false, // Control del modal
      modalPaymentType: null, // Tipo de pago seleccionado en el modal
      catalogoRegimenes: {
        601: "601 - General de Ley Personas Morales",
        603: "603 - Personas Morales con Fines no Lucrativos",
        605: "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios",
        606: "606 - Arrendamiento",
        607: "607 - Régimen de Enajenación o Adquisición de Bienes",
        608: "608 - Demás ingresos",
        610: "610 - Residentes en el Extranjero sin Establecimiento Permanente en México",
        611: "611 - Ingresos por Dividendos (socios y accionistas)",
        612: "612 - Personas Físicas con Actividades Empresariales y Profesionales",
        614: "614 - Ingresos por intereses",
        615: "615 - Régimen de los ingresos por obtención de premios",
        616: "616 - Sin obligaciones fiscales",
        620: "620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
        621: "621 - Incorporación Fiscal",
        622: "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
        623: "623 - Opcional para Grupos de Sociedades",
        624: "624 - Coordinados",
        625: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
        626: "626 - Régimen Simplificado de Confianza"
      }
    };
  },
  computed: {
    // Items que aún pueden ser seleccionados (no tienen liga generada)
    availableItems() {
      return this.data ? this.data.filter(item => !item.hasPaymentLink) : [];
    }
  },
  watch: {
    // Cuando se activa el toggle de factura, verificar datos fiscales
    requiresInvoice(newValue) {
      if (newValue) {
        this.checkBillingData();
      }
    }
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    // Método para formatear números como moneda ($0.00)
    formatCurrency(value) {
      if (value === null || value === undefined) return '$0.00';
      return new Intl.NumberFormat('es-MX', { 
        style: 'currency', 
        currency: 'MXN',
        minimumFractionDigits: 2
      }).format(value);
    },
    
    // Método para formatear fechas en formato yyyy-mm-dd
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    },
    
    // Método para determinar si una fecha ya pasó
    isPastDate(dateString) {
      if (!dateString) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Resetear la hora para comparar solo las fechas
      const paymentDate = new Date(dateString);
      paymentDate.setHours(0, 0, 0, 0);
      return paymentDate <= today;
    },

    // Verificar datos de facturación del estudiante
    async checkBillingData() {
      this.billingDataStatus = 'loading';
      
      try {
        const response = await getBillingData(this.studentId);
        this.billingData = response.data;
        
        // Verificar si hay datos válidos
        if (!this.billingData || !Array.isArray(this.billingData) || this.billingData.length === 0) {
          this.billingDataStatus = 'no-data';
          return;
        }
        
        // Verificar si los datos están actualizados (menos de 6 meses)
        if (this.billingData[0] && this.billingData[0].updated) {
          const updatedDate = new Date(this.billingData[0].updated);
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          
          if (updatedDate < sixMonthsAgo) {
            this.billingDataStatus = 'outdated';
          } else {
            this.billingDataStatus = 'loaded';
          }
        } else {
          this.billingDataStatus = 'loaded';
        }
        
      } catch (error) {
        if (error.response?.status === 404) {
          this.billingDataStatus = 'no-data';
          this.billingData = null;
        } else {
          console.error('Error al verificar datos de facturación:', error);
          this.billingDataStatus = 'no-data';
          this.billingData = null;
        }
      }
    },

    // Mostrar modal de confirmación de pago
    showPaymentModal(type) {
      this.modalPaymentType = type;
      this.showModal = true;
    },

    // Cerrar modal
    closeModal() {
      this.showModal = false;
      this.requiresInvoice = false;
      this.modalPaymentType = null;
      this.billingDataStatus = 'loading';
      this.billingData = null;
    },

    // Redirigir a datos fiscales desde el modal
    goToBillingDataFromModal() {
      this.closeModal();
      this.$router.push('/mis-datos-facturacion');
    },

    // Confirmar el pago desde el modal
    confirmPayment() {
      this.showModal = false;
      this.initiatePayment(this.modalPaymentType);
    },

    // Agrupar pagos
    toggleItemSelection(item) {
      // Solo permitir selección si el item no tiene liga generada
      if (item.hasPaymentLink) return;
      
      const index = this.selectedItems.findIndex(selected => 
        selected.idpayment === item.idpayment
      );
      
      if (index > -1) {
        // Remover si ya está seleccionado
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(item);
      }
    },

    toggleSelectAll() {
      const availableItems = this.availableItems;
      
      if (this.selectedItems.length === availableItems.length) {
        // Si todo está seleccionado, limpiar selección
        this.selectedItems = [];
      } else {
        // Seleccionar todos los items disponibles
        this.selectedItems = [...availableItems];
      }
    },

    calculateTotal() {
      return this.selectedItems.reduce((total, item) => total + item.balance, 0);
    },

    async initiatePayment(type) {
      if (this.selectedItems.length === 0) {
        await this.$alert(
          'Selección requerida',
          'Por favor, seleccione al menos un cargo para pagar.',
          'warning'
        );
        return;
      }

      // Crear ordenes de pago
      const order = {
        idstudent: this.studentId,
        concepts: this.selectedItems.map(item => ({
          idpayment: parseInt(item.idpayment),
          idperiod: item.idperiod,
          idcharge: parseInt(item.idcharge),
          description: item.description,
          charge: parseFloat(item.balance)
        })),
        username: '', // Enviar nulo
        invoice: this.requiresInvoice // Agregar el campo de factura
      };

      this.processPayment(type, order);
    },

    async processPayment(type, order) {
      try {
        const PAYMENTS_URL = process.env.VUE_APP_PAYMENTS_URL;

        const response = await axios.post(`${PAYMENTS_URL}/payments/api/${type}`, order, {
          withCredentials: false,
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });

        if (response.data.url) {
          // Usar el nuevo modal de link de pago en lugar de abrir directamente
          await this.$paymentLink(response.data.url, {
            // Configuración opcional para mostrar/ocultar botones
            showShare: true,
            showCopy: true,
            showOpen: true,
            // Configuración opcional para habilitar/deshabilitar botones
            enableShare: true,  // true = habilitado, false = deshabilitado (gris)
            enableCopy: true,   // true = habilitado, false = deshabilitado (gris)
            enableOpen: true,   // true = habilitado, false = deshabilitado (gris)
            shareOptions: {
              email: true,
              whatsapp: true,  // Se mostrará solo en móvil
              sms: true,       // Se mostrará solo en móvil
              contacts: true   // Se mostrará solo en móvil si el navegador soporta Web Share API
            }
          });

          // Marcar los items seleccionados como pagados y limpiar la selección
          this.markItemsAsPaid();
          this.selectedItems = [];
          this.requiresInvoice = false; // Resetear el toggle
        } else {
          await this.$alert(
            'Error en el pago',
            'No se pudo generar la liga de pago.',
            'error'
          );
        }
      } catch (error) {
        console.error('Error al procesar el pago:', error);
        
        let errorMessage = 'Error desconocido al procesar el pago.';
        
        if (error.response) {
          console.error('Datos del error:', error.response.data);
          console.error('Código de estado:', error.response.status);
          errorMessage = `Error ${error.response.status}: ${error.response.data.message || 'Error del servidor'}`;
        } else if (error.request) {
          console.error('Sin respuesta del servidor:', error.request);
          errorMessage = 'No se recibió respuesta del servidor. Verifique su conexión.';
        } else {
          console.error('Error de configuración:', error.message);
          errorMessage = 'Error al configurar la solicitud.';
        }

        await this.$alert('Error de pago', errorMessage, 'error');
      }
    },

    // Marcar items como que ya tienen liga generada sin recargar
    markItemsAsPaid() {
      this.selectedItems.forEach(selectedItem => {
        // Encontrar el item en el data original y marcarlo como con liga generada
        const dataIndex = this.data.findIndex(item => 
          item.idpayment === selectedItem.idpayment
        );
        
        if (dataIndex > -1) {
          // Marcar como que ya tiene liga generada, pero conservar el monto original
          this.data[dataIndex].hasPaymentLink = true;
        }
      });
    },

    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          // Cargar datos con el ID extraído
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadData() {
      try {
        this.loading = true;
        this.error = null;
        this.noDataAvailable = false;

        const response = await getCalendarioColegiatura({ idstudent: this.studentId });
        
        // Verificar si la respuesta tiene datos
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          this.data = response.data;
        } else {
          // Si api respondió correctamente pero no hay datos
          this.data = [];
          this.noDataAvailable = true;
        }
        
        this.loading = false;
      } catch (error) {
        this.loading = false;
        
        // 404 = URL existe pero no hay datos para este estudiante
        if (error.response && error.response.status === 404) {
          this.noDataAvailable = true;
          return;
        }
        
        // Cualquier otro error = problema de conexión/URL/servidor
        console.error("Error al obtener los datos:", error);
        this.error = "Error al cargar los datos. Por favor, intente nuevamente.";
      }
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    
    closeMenu() {
      this.isMenuOpen = false;
    },
    
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');
        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));
        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    },
    getRegimenTexto(id) {
      return this.catalogoRegimenes[id] || "Régimen no encontrado";
    }
  }
};
</script>